import { z } from 'zod';
/**
 * Realtime protocol.
 *
 * Every envelope is a discriminated union on `t` (type). Client→server frames
 * are validated with Zod at the gateway boundary; server→client frames are
 * constructed exclusively by the server and typed structurally.
 */
/* -------------------------------------------------------------------------- */
/*                              Client -> Server                              */
/* -------------------------------------------------------------------------- */
export const JoinRoomFrame = z.object({
    t: z.literal('JOIN_ROOM'),
    roomId: z.string().min(1).max(64),
});
export const LeaveRoomFrame = z.object({
    t: z.literal('LEAVE_ROOM'),
});
export const HeartbeatFrame = z.object({
    t: z.literal('HEARTBEAT'),
});
/**
 * Optimistic stand edit. The gateway never trusts the payload as-is: it
 * re-reads the persisted stand and broadcasts the authoritative projection.
 */
export const StandUpdateFrame = z.object({
    t: z.literal('STAND_UPDATE'),
    standId: z.string().min(1).max(64),
});
export const ReactionFrame = z.object({
    t: z.literal('REACTION'),
    standId: z.string().min(1).max(64),
    emoji: z.enum(['🔥', '💎', '⭐', '👑', '🫡']),
});
export const SubscribeStandFrame = z.object({
    t: z.literal('SUBSCRIBE_STAND'),
    standId: z.string().min(1).max(64),
});
export const ClientFrame = z.discriminatedUnion('t', [
    JoinRoomFrame,
    LeaveRoomFrame,
    HeartbeatFrame,
    StandUpdateFrame,
    ReactionFrame,
    SubscribeStandFrame,
]);
export function parseClientFrame(raw) {
    let json;
    try {
        json = JSON.parse(raw);
    }
    catch {
        return { ok: false, error: 'Malformed JSON envelope' };
    }
    const parsed = ClientFrame.safeParse(json);
    if (!parsed.success) {
        return { ok: false, error: parsed.error.issues[0]?.message ?? 'Invalid frame' };
    }
    return { ok: true, frame: parsed.data };
}
